import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import { QrCode as QrCodeIcon, Inventory as InventoryIcon } from '@mui/icons-material';
import { getProductWithVariants } from '../../product/services/productVariantService';

/**
 * VariantSelector
 *
 * Reusable dropdown that loads variants for a given productId.
 * Shows stock level and serialized badge per option.
 *
 * Props:
 *   productId   {number|string|null}
 *   value       {Object|null}         – selected variant object
 *   onChange    {fn(variant|null)}
 *   label       {string}
 *   disabled    {bool}
 *   required    {bool}
 *   error       {bool}
 *   helperText  {string}
 *   size        {'small'|'medium'}
 */
const VariantSelector = ({
  productId,
  value = null,
  onChange,
  label = 'Select Variant',
  disabled = false,
  required = false,
  error = false,
  helperText = '',
  size = 'small',
}) => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!productId) {
      setVariants([]);
      setLoadError(false);
      if (value) onChange(null);
      return;
    }
    setLoading(true);
    setLoadError(false);
    getProductWithVariants(productId)
      .then((res) => {
        const v =
          res?.payload?.Variants ||
          res?.payload?.variants ||
          res?.Variants ||
          res?.variants ||
          [];
        // Map PascalCase fields to camelCase for dropdown rendering
        const mapped = Array.isArray(v)
          ? v.map(variant => ({
              ...variant,
              sku: variant.sku || variant.SKU || '',
              salePrice: variant.salePrice ?? variant.SalePrice ?? 0,
              stockQuantity: variant.stockQuantity ?? variant.StockQuantity ?? 0,
              availableStock: variant.availableStock ?? variant.AvailableStock ?? variant.stockQuantity ?? variant.StockQuantity ?? 0,
              variantName: variant.variantName || variant.VariantName || '',
              isSerializedProduct: variant.isSerializedProduct ?? variant.IsSerializedProduct ?? false,
              id: variant.id ?? variant.Id,
            }))
          : [];
        setVariants(mapped);
      })
      .catch(() => {
        setVariants([]);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [productId]);

  // Reset selection if productId changes and selected variant doesn't belong
  useEffect(() => {
    if (value && !variants.some((v) => v.id === value.id)) {
      onChange(null);
    }
  }, [variants, value, onChange]);

  return (
    <Autocomplete
      options={variants}
      value={value}
      loading={loading}
      disabled={disabled || !productId}
      getOptionLabel={(option) => option.variantName || ''}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      onChange={(_, newValue) => onChange(newValue)}
      noOptionsText={
        loading
          ? 'Loading variants…'
          : loadError
          ? 'Failed to load variants. Check connection.'
          : !productId
          ? 'Select a product first'
          : 'No variants found for this product'
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ flexDirection: 'column', alignItems: 'flex-start !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Typography variant="body2" fontWeight={600}>{option.variantName}</Typography>
            {option.isSerializedProduct && (
              <Chip icon={<QrCodeIcon />} label="IMEI" size="small" color="info" />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              SKU: {option.sku}
            </Typography>
            <Typography
              variant="caption"
              color={
                (option.availableStock ?? option.stockQuantity ?? 0) <= 0
                  ? 'error'
                  : 'success.main'
              }
              fontWeight={600}
            >
              <InventoryIcon sx={{ fontSize: 10, mr: 0.25 }} />
              {option.availableStock ?? option.stockQuantity ?? 0} in stock
            </Typography>
            <Typography variant="caption" color="text.secondary">
              €{Number(option.salePrice || 0).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error || loadError}
          helperText={
            helperText ||
            (loadError ? 'Could not load variants — check network/API' : !productId ? 'Select a product first' : '')
          }
          size={size}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default VariantSelector;
